// Copyright (c) 2026 BlueRock Security, Inc.
// All rights reserved.
//
// BLUEROCK SECURITY, INC. PROPRIETARY AND CONFIDENTIAL INFORMATION

import type { CSSProperties, ReactNode } from 'react';
import { Card, Flex, Skeleton, Typography, theme } from 'antd';
import { AlertTriangle } from 'lucide-react';
import { isDarkSurface } from './chartTheme';
import { BarsViz, CompositionViz, ListViz, MeterViz, StatViz, TrendViz } from './viz';
import { formatMetricValue } from './types';
import type { MetricCardDescriptor, MetricEnvelope, MetricShape, MetricVizType } from './types';

const { Text } = Typography;

export interface MetricCardProps {
  descriptor: MetricCardDescriptor;
  /** The metric envelope (ADR 0035). Omit while loading or on error. */
  data?: MetricEnvelope;
  loading?: boolean;
  /** Short user-facing message; renders the card's error state. */
  error?: string;
  /**
   * Consumer-injected header controls (e.g. customize-mode remove/drag
   * affordances). Rendered after the window caption.
   */
  headerExtra?: ReactNode;
  /**
   * Optional icon rendered in the header's tinted chip — gives a card a
   * visual anchor and identity. The consumer supplies the node (and its
   * color; the chip supplies the colorPrimaryBg tint behind it).
   */
  icon?: ReactNode;
  /**
   * Makes the whole card an affordance (hover ring, pointer, Enter key) —
   * the Board's drill-down to the metric detail view. Row links inside list
   * cards still navigate on their own (they stop propagation).
   */
  onClick?: () => void;
  style?: CSSProperties;
  className?: string;
}

/** Which envelope field each viz type consumes — the one-code-path check. */
const VIZ_SHAPE: Record<MetricVizType, MetricShape> = {
  stat: 'scalar',
  meter: 'scalar',
  trend: 'series',
  bars: 'groups',
  composition: 'groups',
  list: 'rows',
};

/**
 * The generic dashboard card (ADR 0035): every card — shipped default,
 * catalog pick, user-created — renders through this one component from a
 * descriptor plus a metric envelope. Presentation-only: data, loading, and
 * error arrive as props; fetching, layout, and persistence are the
 * consumer's.
 */
export function MetricCard({
  descriptor,
  data,
  loading,
  error,
  headerExtra,
  icon,
  onClick,
  style,
  className,
}: MetricCardProps) {
  const { token } = theme.useToken();

  // Header context: the window caption ("last 7d"), plus the window total for
  // trends that ask for it. No category chip — category organizes the
  // add-panel, not the board (the data is the loudest thing on a card).
  // Point-in-time metrics (no window) simply show no caption.
  const captionParts: string[] = [];
  if (descriptor.query?.window) {
    captionParts.push(`last ${descriptor.query.window}`);
  }
  if (descriptor.viz.type === 'trend' && descriptor.viz.options?.showTotal && data?.series?.length) {
    const total = data.series.reduce((sum, p) => sum + p.v, 0);
    captionParts.push(`${formatMetricValue(total, data.unit)} total`);
  }

  const headerActions = (
    <Flex align="center" gap={8} onClick={e => e.stopPropagation()}>
      {captionParts.length > 0 && (
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }} className="tabular-nums">
          {captionParts.join(' · ')}
        </Text>
      )}
      {headerExtra}
    </Flex>
  );

  // ONE chrome for all six viz types: every card renders inside the same
  // shell; only the body differs. Stat is not special. SaaS treatment
  // (2026-09-01): the header lives INSIDE the card body — tinted icon chip +
  // eyebrow title demoted to secondary (the data is the loudest thing on a
  // card), caption + consumer controls right — over a soft-shadowed hairline
  // shell, no antd Card header bar.
  let body: ReactNode;
  if (error) {
    body = (
      <Flex align="center" gap={8} style={{ color: token.colorWarning }}>
        <AlertTriangle size={14} strokeWidth={1.75} color={token.colorWarning} />
        <Text type="warning" style={{ fontSize: token.fontSizeSM }}>
          {error}
        </Text>
      </Flex>
    );
  } else if (loading || !data) {
    body = <Skeleton active paragraph={{ rows: 2 }} title={false} />;
  } else if (!hasShape(data, VIZ_SHAPE[descriptor.viz.type])) {
    body = (
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        No data yet
      </Text>
    );
  } else {
    switch (descriptor.viz.type) {
      case 'stat':
        body = <StatViz descriptor={descriptor} data={data} />;
        break;
      case 'meter':
        body = <MeterViz descriptor={descriptor} data={data} />;
        break;
      case 'trend':
        body = <TrendViz descriptor={descriptor} data={data} />;
        break;
      case 'bars':
        body = <BarsViz descriptor={descriptor} data={data} />;
        break;
      case 'composition':
        body = <CompositionViz descriptor={descriptor} data={data} />;
        break;
      case 'list':
        body = <ListViz descriptor={descriptor} data={data} />;
        break;
    }
  }

  return (
    <Card
      hoverable={!!onClick}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'link' : undefined}
      onKeyDown={onClick ? e => e.key === 'Enter' && onClick() : undefined}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: token.boxShadowTertiary,
        ...style,
      }}
      styles={{
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          padding: `${token.paddingSM}px ${token.padding}px`,
        },
      }}
      className={className}
    >
      <Flex align="center" gap={token.marginXS} style={{ marginBottom: token.marginXS }}>
        {icon && (
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: token.borderRadiusSM,
              background: token.colorPrimaryBg,
              // Icons ride currentColor. Dark's generated colorPrimary
              // (#035edc-ish) disappears against the dark colorPrimaryBg
              // chip, so dark uses the ramp's brighter hover step.
              color: isDarkSurface(token) ? token.colorPrimaryHover : token.colorPrimary,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </span>
        )}
        <Text
          ellipsis
          type="secondary"
          style={{ flex: 1, minWidth: 0, fontSize: token.fontSize, fontWeight: 500 }}
        >
          {descriptor.title}
        </Text>
        {headerActions}
      </Flex>
      {body}
    </Card>
  );
}

function hasShape(data: MetricEnvelope, shape: MetricShape): boolean {
  switch (shape) {
    case 'scalar':
      return data.scalar != null;
    case 'series':
      return data.series != null;
    case 'groups':
      return data.groups != null;
    case 'rows':
      return data.rows != null;
  }
}

export default MetricCard;
